// Lays a claim's data out to match the real Rothbury "Make a Claim" form's
// section structure (see the reference PDF Janelle attached) -- FormsByAir
// API access is still pending, so this is what she prints/re-keys or
// attaches until that lands. Field grouping is shared with the on-screen
// review preview in InsuranceClaimsTab.tsx via claimSections.ts, so what
// she reads before submitting matches what's actually in the PDF. Doesn't
// reproduce Rothbury's own logo or legal declaration text -- just their
// field labels and grouping.
import jsPDF from "jspdf";
import "jspdf-autotable";
import { supabase } from "@/lib/supabase";
import {
  InsuranceIncident,
  InsuranceClaim,
  IncidentDamageMarker,
  fetchIncidentPhotos,
  fetchIncidentDamageMarkers,
  fetchIncidentThirdParties,
} from "@/lib/incidentReports";
import { ClaimSection, buildClaimSections, loadClaimData } from "@/lib/claimSections";
import { DAMAGE_DIAGRAM_VIEWS, resolveOverlayAssetKey, overlayPublicUrl, renderDiagramWithMarkers } from "@/lib/damageDiagram";
import { formatDate } from "@/lib/dates";

function sanitizeFilename(s: string): string {
  return s.replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "");
}

// Driver photos sometimes arrive in a format jsPDF can't embed directly
// (iPhones default to HEIC) -- the browser's <img> tag can often still
// display it (Safari/WebKit has native OS-level HEIC decoding), which is
// why just measuring width/height off the raw bytes worked, but jsPDF has
// no such decoder and mangled the raw bytes into visual noise when handed
// them as-is. Re-rasterising through a canvas first (same trick
// damageDiagram.ts uses) guarantees jsPDF always receives a real,
// guaranteed-decodable JPEG regardless of what the photo was uploaded as.
async function loadImageAsDataUrl(url: string): Promise<{ dataUrl: string; width: number; height: number } | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const img = await new Promise<HTMLImageElement | null>((resolve) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => resolve(null);
      el.src = objectUrl;
    });
    URL.revokeObjectURL(objectUrl);
    if (!img || !img.naturalWidth) return null;

    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0);
    return { dataUrl: canvas.toDataURL("image/jpeg", 0.85), width: canvas.width, height: canvas.height };
  } catch {
    return null;
  }
}

async function buildClaimPdfDoc(incident: InsuranceIncident, claim: InsuranceClaim): Promise<{ doc: jsPDF; fileName: string }> {
  const [data, photos, damageMarkers, thirdParties] = await Promise.all([
    loadClaimData(incident, claim),
    fetchIncidentPhotos(incident.id),
    fetchIncidentDamageMarkers(incident.id),
    fetchIncidentThirdParties(incident.id),
  ]);
  const sections = buildClaimSections(data);
  const overlayAssetKey = await resolveOverlayAssetKey(data.vehicle?.vehicle_type ?? null);
  const thirdPartyOverlayKeys = await Promise.all(
    thirdParties.map((tp) => resolveOverlayAssetKey(tp.overlay_vehicle_type))
  );

  const doc = new jsPDF();
  let y = 18;

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("Insurance Claim -- Make a Claim", 14, y);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Generated ${formatDate(new Date())} from Taxis Gold & Black DMS`, 14, y + 6);
  y += 14;

  const addSection = (section: ClaimSection) => {
    if (section.rows.length === 0) return;
    if (y > 265) {
      doc.addPage();
      y = 18;
    }
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text(section.title, 14, y);
    doc.setFont("helvetica", "normal");
    (doc as any).autoTable({
      body: section.rows,
      startY: y + 3,
      margin: { left: 14, right: 14 },
      styles: { fontSize: 9, cellPadding: 1.5 },
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 55 }, 1: { cellWidth: "auto" } },
      theme: "plain",
    });
    y = (doc as any).lastAutoTable.finalY + 8;
  };

  sections.forEach(addSection);

  if (photos.length > 0) {
    if (y > 250) {
      doc.addPage();
      y = 18;
    }
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.text("Photos", 14, y);
    doc.setFont("helvetica", "normal");
    y += 6;

    const thumbWidth = 55;
    const gap = 5;
    const perRow = 3;
    let col = 0;
    let rowMaxHeight = 0;

    for (const photo of photos) {
      const url = supabase.storage.from("incident-reports").getPublicUrl(photo.storage_path).data.publicUrl;
      const img = await loadImageAsDataUrl(url);
      if (!img || img.width === 0) continue;

      const thumbHeight = (img.height / img.width) * thumbWidth;
      if (y + thumbHeight > 280) {
        doc.addPage();
        y = 18;
        col = 0;
        rowMaxHeight = 0;
      }

      const x = 14 + col * (thumbWidth + gap);
      doc.addImage(img.dataUrl, "JPEG", x, y, thumbWidth, thumbHeight);
      rowMaxHeight = Math.max(rowMaxHeight, thumbHeight);
      col++;
      if (col >= perRow) {
        col = 0;
        y += rowMaxHeight + gap;
        rowMaxHeight = 0;
      }
    }
    if (col > 0) y += rowMaxHeight + gap;
  }

  const addDiagramSection = async (heading: string, assetKey: string, markers: IncidentDamageMarker[]) => {
    const diagramWidth = 85;
    for (const { view, label } of DAMAGE_DIAGRAM_VIEWS) {
      const viewMarkers = markers.filter((m) => m.view === view);
      if (viewMarkers.length === 0) continue;

      const rendered = await renderDiagramWithMarkers(overlayPublicUrl(assetKey, view), viewMarkers);
      if (!rendered) continue;
      const diagramHeight = (rendered.height / rendered.width) * diagramWidth;

      if (y + diagramHeight > 270) {
        doc.addPage();
        y = 18;
      }
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text(`${heading} -- ${label}`, 14, y);
      doc.setFont("helvetica", "normal");
      y += 5;
      doc.addImage(rendered.dataUrl, "PNG", 14, y, diagramWidth, diagramHeight);

      const legendX = 14 + diagramWidth + 8;
      let legendY = y + 4;
      doc.setFontSize(8);
      viewMarkers.forEach((m, i) => {
        doc.text(`${i + 1}. ${m.damage_type} (${m.severity})${m.notes ? ` -- ${m.notes}` : ""}`, legendX, legendY, { maxWidth: 90 });
        legendY += 5;
      });

      y += diagramHeight + 8;
    }
  };

  if (overlayAssetKey && damageMarkers.length > 0) {
    await addDiagramSection("Damage Diagram", overlayAssetKey, damageMarkers.filter((m) => !m.third_party_id));
  }

  for (let i = 0; i < thirdParties.length; i++) {
    const tp = thirdParties[i];
    const tpAssetKey = thirdPartyOverlayKeys[i];
    const tpMarkers = damageMarkers.filter((m) => m.third_party_id === tp.id);
    if (!tpAssetKey || tpMarkers.length === 0) continue;
    await addDiagramSection(`Damage Diagram -- ${tp.other_driver_name || "Other Party"}`, tpAssetKey, tpMarkers);
  }

  const fileName = `${sanitizeFilename(incident.driver_name || "claim")}_${incident.incident_date}_Claim.pdf`;
  return { doc, fileName };
}

export async function generateClaimPdf(incident: InsuranceIncident, claim: InsuranceClaim): Promise<void> {
  const { doc, fileName } = await buildClaimPdfDoc(incident, claim);
  doc.save(fileName);
}

/** Same PDF as generateClaimPdf, but returned as base64 for emailing as an
 *  attachment instead of triggering a browser download. */
export async function getClaimPdfBase64(
  incident: InsuranceIncident,
  claim: InsuranceClaim
): Promise<{ base64: string; fileName: string }> {
  const { doc, fileName } = await buildClaimPdfDoc(incident, claim);
  const base64 = doc.output("datauristring").split(",")[1] || "";
  return { base64, fileName };
}
